"use client";
import { BuyerDetails, getBuyerData } from "@/utils/get-buyer-data";
import React from "react";
import { useAuth } from "./use-auth";

export interface BuyerActions {
	shortlistUnits: string[];
	requestVisitUnits: string[];
	shortlistProjects: string[];
	requestVisitProjects: string[];
}
export interface BuyerRequestContextValue {
	data?: BuyerDetails | null;
	error?: string | null;
	actions: BuyerActions;
	refetch: (_arg0?: string) => Promise<void>;
	setActions: (actions: BuyerActions) => void;
}

export const BuyerRequestContext = React.createContext<BuyerRequestContextValue | null>(null);

export const BuyerRequestProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { user } = useAuth();
	const [data, setData] = React.useState<BuyerDetails | null>(null);
	const [error, setError] = React.useState<string | null>(null);
	const [actions, setActions] = React.useState<BuyerActions>({
		shortlistUnits: [],
		requestVisitUnits: [],
		shortlistProjects: [],
		requestVisitProjects: [],
	});

	const refetch = React.useCallback(
		async (buyerId?: string) => {
			buyerId = buyerId ?? user?.groups.find(group => group.relationship === "buyer")?.id;
			if (buyerId) {
				try {
					const response = await getBuyerData({ id: buyerId });
					setData(response.data);
					setError(response.error);
				} catch (error: unknown) {
					if (error instanceof Error) {
						setError(error.message);
					} else {
						setError("An error occurred");
					}
				}
			}
		},
		[user?.groups],
	);

	React.useEffect(() => {
		if (user?.groups) {
			refetch();
		} else setData(null);
	}, [refetch, user?.groups]);

	// Memoize context value to prevent unnecessary re-renders of consuming components
	const contextValue: BuyerRequestContextValue = React.useMemo(
		() => ({
			data,
			error,
			refetch,
			actions,
			setActions,
		}),
		[data, error, refetch, actions, setActions],
	);

	return <BuyerRequestContext.Provider value={contextValue}>{children}</BuyerRequestContext.Provider>;
};

export const useBuyerRequestContext = (): BuyerRequestContextValue => {
	const context = React.useContext(BuyerRequestContext);
	if (context === null) {
		throw new Error("useBuyerRequestContext must be used within a BuyerRequestProvider");
	}
	return context;
};
